import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Zap, Wrench, Sparkles, ClipboardCheck, Truck, CircleDot, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Panel } from "@/pages/shared";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const CATEGORIES = ["power", "repair", "cleaning", "inspection", "delivery", "other"];
const PRIORITIES = ["laag", "normaal", "hoog", "dringend"];
const STATUSES = ["open", "assigned", "busy", "done", "cancelled"];
const CAT_ICON = { power: Zap, repair: Wrench, cleaning: Sparkles, inspection: ClipboardCheck, delivery: Truck, other: CircleDot };
const PRIO_CLS = {
  laag: "bg-slate-100 text-slate-600 border-slate-200",
  normaal: "bg-sky-50 text-sky-700 border-sky-200",
  hoog: "bg-amber-50 text-amber-700 border-amber-200",
  dringend: "bg-rose-50 text-rose-700 border-rose-200",
};
const STATUS_CLS = {
  open: "text-slate-600", assigned: "text-sky-600", busy: "text-amber-600",
  done: "text-emerald-600", cancelled: "text-rose-600",
};

const blank = () => ({ type: "other", title: "", pitch_label: "", detail: "", priority: "normaal", due_date: "", assigned_to: "" });

export default function Jobs() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [controllers, setControllers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [assignee, setAssignee] = useState("all");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { status: tab };
      if (assignee !== "all") params.assignee = assignee;
      const [j, c] = await Promise.all([
        api.get("/jobs", { params }),
        api.get("/jobs/controllers"),
      ]);
      setRows(j.data || []);
      setControllers(c.data || []);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [tab, assignee]);

  useEffect(() => { load(); }, [load]);

  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = (j) => j.due_date && j.due_date < today && !["done", "cancelled"].includes(j.status);

  const openNew = () => { setForm(blank()); setEditId(null); setOpen(true); };
  const openEdit = (j) => {
    setForm({ type: j.type || "other", title: j.title || "", pitch_label: j.pitch_label || "",
      detail: j.detail || "", priority: j.priority || "normaal", due_date: j.due_date || "",
      assigned_to: j.assigned_to || "" });
    setEditId(j.id); setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error(t("job_title")); return; }
    setBusy(true);
    try {
      const body = { ...form, pitch_label: form.pitch_label || null, detail: form.detail || null,
        due_date: form.due_date || null, assigned_to: form.assigned_to || null };
      if (editId) await api.patch(`/jobs/${editId}`, body);
      else await api.post("/jobs", body);
      setOpen(false);
      toast.success(t("job_saved"));
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const assign = async (id, userId) => {
    try { await api.patch(`/jobs/${id}/assign`, { user_id: userId || null }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/jobs/${id}/status`, { status }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (j) => {
    if (!window.confirm(t("job_delete_confirm"))) return;
    try { await api.delete(`/jobs/${j.id}`); toast.success(t("job_deleted")); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const TabBtn = ({ id, label }) => (
    <button type="button" onClick={() => setTab(id)} data-testid={`jobs-tab-${id}`}
      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${tab === id ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:bg-secondary"}`}>
      {label}
    </button>
  );

  return (
    <Panel
      title={t("nav_jobs")}
      testid="jobs-panel"
      action={<Button onClick={openNew} data-testid="job-new-button"><Plus className="h-4 w-4 mr-1" />{t("job_new")}</Button>}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4">
        <div className="inline-flex gap-1 rounded-lg bg-secondary/50 p-1" data-testid="jobs-tabs">
          <TabBtn id="active" label={t("tab_active")} />
          <TabBtn id="done" label={t("tab_done")} />
          <TabBtn id="all" label={t("tab_all")} />
        </div>
        <div className="w-56">
          <Select value={assignee} onValueChange={setAssignee}>
            <SelectTrigger data-testid="jobs-filter-assignee"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("job_all_assignees")}</SelectItem>
              {controllers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? null : rows.length === 0 ? (
        <div className="p-10 text-center text-sm text-muted-foreground" data-testid="jobs-empty">{t("jobs_none")}</div>
      ) : (
        <div className="p-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("job_task")}</TableHead>
                <TableHead>{t("pitch")}</TableHead>
                <TableHead>{t("job_priority")}</TableHead>
                <TableHead>{t("job_due")}</TableHead>
                <TableHead>{t("job_assignee")}</TableHead>
                <TableHead>{t("job_status")}</TableHead>
                <TableHead className="text-right">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((j) => {
                const Icon = CAT_ICON[j.type] || CircleDot;
                const overdue = isOverdue(j);
                return (
                  <TableRow key={j.id} data-testid={`job-row-${j.id}`} className={j.status === "done" || j.status === "cancelled" ? "opacity-60" : ""}>
                    <TableCell className="font-medium">
                      <span className="inline-flex items-center gap-2">
                        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span>{j.title}</span>
                      </span>
                      <span className="block text-[11px] uppercase tracking-wide text-muted-foreground">{t(`cat_${j.type}`)}</span>
                      {j.detail && <span className="block text-xs text-muted-foreground">{j.detail}</span>}
                    </TableCell>
                    <TableCell><span className="font-semibold text-primary">{j.pitch_label || "—"}</span></TableCell>
                    <TableCell>
                      <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold ${PRIO_CLS[j.priority] || PRIO_CLS.normaal}`} data-testid={`job-prio-${j.id}`}>
                        {t(`prio_${j.priority}`)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {j.due_date ? (
                        <span className={`inline-flex items-center gap-1 text-sm ${overdue ? "font-semibold text-rose-600" : "text-foreground"}`} data-testid={`job-due-${j.id}`}>
                          {overdue && <AlertTriangle className="h-3.5 w-3.5" />}
                          {j.due_date}
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <select value={j.assigned_to || ""} onChange={(e) => assign(j.id, e.target.value)}
                        data-testid={`job-assign-${j.id}`}
                        className={`rounded-md border px-2 py-1.5 text-sm bg-background ${j.assigned_to ? "border-emerald-500/60" : "border-border"}`}>
                        <option value="">{t("job_unassigned")}</option>
                        {controllers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </TableCell>
                    <TableCell>
                      <select value={j.status} onChange={(e) => setStatus(j.id, e.target.value)}
                        data-testid={`job-status-${j.id}`}
                        className={`rounded-md border border-border px-2 py-1.5 text-sm bg-background font-semibold ${STATUS_CLS[j.status] || ""}`}>
                        {STATUSES.map((s) => <option key={s} value={s}>{t(`status_${s}`)}</option>)}
                      </select>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(j)} data-testid={`job-edit-${j.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" className="ml-1 text-rose-600 hover:text-rose-700" onClick={() => remove(j)} data-testid={`job-delete-${j.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="job-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? t("job_edit") : t("job_new")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("job_title")}</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="job-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("job_category")}</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="job-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`cat_${c}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("job_priority")}</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="job-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{t(`prio_${p}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("pitch")}</Label>
                <Input value={form.pitch_label} onChange={(e) => setForm({ ...form, pitch_label: e.target.value })} data-testid="job-pitch-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("job_due")}</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="job-due-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("job_assignee")}</Label>
              <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="job-assignee-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("job_unassigned")}</SelectItem>
                  {controllers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("job_detail")}</Label>
              <Input value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} data-testid="job-detail-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} disabled={!form.title.trim() || busy} data-testid="job-save-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
